import { errorMessage } from "@atlas/shared";
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { X } from "lucide-react";
import { getStoreItems } from "../api/store";
import { giveAward } from "../api/social";
import { iconByName } from "../lib/achievement-icons";

// ─── AwardPicker ────────────────────────────────────────────────────────────────
// Popover listing every store item of kind "award" (packages/db seeds/store.ts:
// award-cairn, award-first-light, award-summit-salute, award-golden-boot) from
// GET /store/items, so never-purchased awards are still visible. Owned=0 rows
// show "Buy in store" instead of a give action. Rendered by PostCard, absolutely
// positioned under its trigger button.

export interface AwardPickerProps {
  postId: string;
  onClose: () => void;
}

export function AwardPicker({ postId, onClose }: AwardPickerProps) {
  const queryClient = useQueryClient();
  const [given, setGiven] = React.useState<string | null>(null);

  const itemsQuery = useQuery({
    queryKey: ["store/items"],
    queryFn: getStoreItems,
  });

  const awardItems = (itemsQuery.data?.items ?? []).filter((i) => i.kind === "award");

  const giveMutation = useMutation({
    mutationFn: (itemSlug: string) => giveAward(postId, itemSlug),
    onSuccess: (_data, itemSlug) => {
      setGiven(itemSlug);
      void queryClient.invalidateQueries({ queryKey: ["social/feed"] });
      void queryClient.invalidateQueries({ queryKey: ["explorers"] });
      void queryClient.invalidateQueries({ queryKey: ["store/inventory"] });
      void queryClient.invalidateQueries({ queryKey: ["store/items"] });
    },
  });

  const error =
    giveMutation.isError ? errorMessage(giveMutation.error, "Couldn't give that award. Try again.") : null;

  return (
    <div
      role="dialog"
      className="atlas-glass"
      style={{
        position: "absolute",
        top: "calc(100% + 8px)",
        right: 0,
        zIndex: 40,
        width: 260,
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow-lg)",
        padding: 14,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <span className="eyebrow" style={{ fontSize: 10 }}>Give an award</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 2 }}
        >
          <X size={14} />
        </button>
      </div>

      {itemsQuery.isLoading ? (
        <p style={{ fontSize: 12.5, color: "var(--text-muted)", margin: 0 }}>Loading…</p>
      ) : awardItems.length === 0 ? (
        <p style={{ fontSize: 12.5, color: "var(--text-muted)", margin: 0 }}>
          No awards yet.{" "}
          <Link to="/store" style={{ color: "var(--sky-300)" }}>Browse the store</Link>
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {awardItems.map((item) => {
            const owned = item.ownedQuantity > 0;
            const points = item.metadata.pointsToRecipient ?? 0;
            const wasGiven = given === item.slug;
            return (
              <div
                key={item.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 10px",
                  borderRadius: "var(--radius-md)",
                  background: "var(--surface-card)",
                  border: "1px solid var(--border-subtle)",
                }}
              >
                <span style={{ color: "var(--gold-400)", flexShrink: 0 }}>{iconByName(item.metadata.icon, 18)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>{item.name}</div>
                  <div className="tnum" style={{ fontSize: 10.5, color: "var(--text-faint)" }}>
                    {owned
                      ? `×${item.ownedQuantity} owned · +${points} Waypoints`
                      : `+${points} Waypoints`}
                  </div>
                </div>
                {owned ? (
                  <button
                    type="button"
                    disabled={giveMutation.isPending || wasGiven}
                    onClick={() => giveMutation.mutate(item.slug)}
                    style={{
                      flexShrink: 0,
                      height: 28,
                      padding: "0 10px",
                      borderRadius: "var(--radius-pill)",
                      border: "1px solid var(--border-gold)",
                      background: wasGiven ? "var(--accent-soft)" : "var(--accent)",
                      color: wasGiven ? "var(--gold-300)" : "var(--text-on-gold)",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: wasGiven ? "default" : "pointer",
                    }}
                  >
                    {wasGiven ? "Given" : "Give"}
                  </button>
                ) : (
                  <Link
                    to="/store"
                    style={{ flexShrink: 0, fontSize: 12, color: "var(--sky-300)", textDecoration: "none" }}
                  >
                    Buy in store
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      )}

      {error && (
        <p style={{ fontSize: 11.5, color: "var(--status-danger)", marginTop: 8, marginBottom: 0 }}>{error}</p>
      )}
    </div>
  );
}
